<template>
  <div>
    <div class="d-flex justify-space-between align-center mb-4">
      <div class="subtitle1">
        {{ $t("modals.applyGlobalMspPolicy.selectWorkspaces") }}
      </div>
      <div>
        <cui-filter-wrapper
          :show-clear-button="showClearFilters"
          @clear-filters-clicked="clearFilters()"
        >
          <v-select
            v-model="localFilters.policiesFilter"
            :items="globalPolicies"
            :menu-props="{ maxHeight: '300' }"
            class="filter-menu addon-filter mr-2"
            menu-icon="icon-arrow-down-xs"
            :class="{ 'filter-active': localFilters.policiesFilter.length > 0 }"
            :placeholder="$t('modals.applyGlobalMspPolicy.policy')"
            density="compact"
            variant="outlined"
            rounded
            item-value="id"
            item-title=""
            multiple
          >
            <template #selection="{ index }">
              <span v-if="index === 0" class="body2">
                {{ $t("modals.applyGlobalMspPolicy.policy") }}
              </span>
            </template>
            <template #label>
              <span v-if="localFilters.policiesFilter.length" class="filter-label">
                {{ localFilters.policiesFilter.length }}
              </span>
            </template>
            <template #item="{ item, props }">
              <v-list-item v-bind="props">
                <template v-slot:prepend="{ isActive }">
                  <v-list-item-action start>
                    <v-checkbox-btn density="compact" :model-value="isActive"></v-checkbox-btn>
                  </v-list-item-action>
                </template>

                <template v-slot:title>
                  <span>{{ item.raw.name }}</span>
                </template>
              </v-list-item>
            </template>
          </v-select>
          <v-select
            v-model="localFilters.labels"
            :items="labels"
            :menu-props="{ maxHeight: '300' }"
            class="filter-menu addon-filter mr-2"
            menu-icon="icon-arrow-down-xs"
            :class="{ 'filter-active': localFilters.labels?.length }"
            :placeholder="$t('manageWorkspaces.filters.labels')"
            density="compact"
            variant="outlined"
            multiple
            rounded
            item-value="id"
            item-title=""
          >
            <template #selection="{ index }">
              <span v-if="index === 0" class="body2">
                {{ $t("manageWorkspaces.filters.labels") }}
              </span>
            </template>
            <template #label>
              <span v-if="localFilters.labels.length" class="filter-label">{{
                localFilters.labels.length
              }}</span>
            </template>
            <template #item="{ item, props }">
              <v-list-item v-bind="props">
                <template v-slot:prepend="{ isActive }">
                  <v-list-item-action start>
                    <v-checkbox-btn density="compact" :model-value="isActive"></v-checkbox-btn>
                  </v-list-item-action>
                </template>

                <template v-slot:title>
                  <div class="d-flex align-center label-popup-block">
                    <div
                      class="color-preview mr-2"
                      :style="{ 'background-color': item.raw.color }"
                    />
                    <span>{{ item.raw.name }}</span>
                  </div>
                </template>
              </v-list-item>
            </template>
          </v-select>
          <v-text-field
            density="compact"
            rounded
            min-width="200"
            clear-icon="$x"
            :placeholder="$t('general.search')"
            :aria-label="$t('general.search')"
            prepend-inner-icon="$search"
            data-testid="select-hierarchy-search"
            clearable
            variant="filled"
            class="search-filter"
            bg-color="indigo-faint"
            :hide-details="true"
            v-model="localFilters.search"
          />
        </cui-filter-wrapper>
      </div>
    </div>
    <table-wrapper>
      <v-data-table-virtual
        :headers="headers"
        :items="workspaces"
        item-selectable="selectable"
        item-value="workspaceId"
        return-object
        :items-per-page="pagination.pageSize"
        :items-length="total"
        show-select
        disable-sort
        fixed-header
        v-model="selectedItems"
        :loading="loading"
      >
        <template #item="{ item, index, internalItem, isSelected, toggleSelect }">
          <tr
            :key="index"
            :id="index.toString()"
            :data-testid="`table-row-${index}`"
            v-intersect="onIntersect"
            class="table-row"
            :class="{ 'v-row--selected': isSelected(internalItem) }"
          >
            <td class="w-5">
              <v-checkbox-btn
                :model-value="isSelected(internalItem)"
                color="primary"
                density="compact"
                @update:model-value="toggleSelect(internalItem)"
              ></v-checkbox-btn>
            </td>
            <td class="w-35">
              <div class="d-flex align-center py-1">
                <v-icon class="workspace-icon" size="40">{{ iconsMap[item.type] }}</v-icon>
                <div class="ml-3">
                  <div class="subtitle2 white-space-normal">
                    {{ item.displayName }}
                  </div>
                  <div class="caption text-indigo-medium white-space-normal">
                    {{ item.workspaceId }}
                  </div>
                </div>
              </div>
            </td>
            <td class="policy-name-col">
              {{ item?.mspPolicyName }}
            </td>
            <td class="labels-col">
              <div v-if="item.labels && item.labels.length" class="mb-1">
                <v-chip
                  v-for="label in item.labels"
                  :key="label.id"
                  variant="flat"
                  label
                  class="mr-1 mt-1"
                  :color="label.color"
                >
                  <div class="d-flex caption align-center">
                    {{ label.name }}
                  </div>
                </v-chip>
              </div>
            </td>
          </tr>
        </template>
      </v-data-table-virtual>
    </table-wrapper>
  </div>
</template>

<script lang="ts">
import { computed, onMounted, type PropType, type Ref, ref, watch } from "vue";
import { handleVirtualScrollData } from "@coronet/cui";
import type { Pagination } from "@/types.ts";
import type { DialogDataConfig } from "@/_store/dialogs.module.ts";
import { useI18n } from "vue-i18n";
import {
  type ApplyPolicyFilters,
  defaultApplyPolicyFilters,
  type GlobalPolicyListItem,
  type GlobalPolicyWorkspaceToAssign,
  useMspGlobalPoliciesStore,
} from "@/_store/msp/global-policies/msp-global-policies.module.ts";
import TableWrapper from "@/components/TableWrapper.vue";
import { WorkspaceType } from "@/constants/workplaces.ts";
import { useMspLabelsStore } from "@/_store/msp/msp-labels.module.ts";
import { storeToRefs } from "pinia";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import { CuiFilterWrapper } from "@coronet/cui";
import { useInfiniteScroll } from "@/composables/useInfiniteScroll";

export default {
  components: {
    CuiFilterWrapper,
    TableWrapper,
  },
  props: {
    config: {
      type: Object as PropType<DialogDataConfig<GlobalPolicyListItem>>,
      required: true,
    },
  },
  emits: ["update:localValue"],
  setup(props, { emit }) {
    const globalPolicyStore = useMspGlobalPoliciesStore();
    const labelsStore = useMspLabelsStore();
    const globalPolicies: Ref<GlobalPolicyListItem[]> = ref([]);
    const localFilters: Ref<ApplyPolicyFilters> = ref(cloneDeep(defaultApplyPolicyFilters));
    let workspaceTimer: ReturnType<typeof setTimeout>;
    const total = ref(0);
    const selectedItems = ref<GlobalPolicyWorkspaceToAssign[]>([]);
    const workspaces = ref<GlobalPolicyWorkspaceToAssign[]>([]);
    const pagination = ref<Pagination>({
      page: 0,
      pageSize: 10,
    });
    const i18n = useI18n();
    const loading = ref(false);
    const { labels } = storeToRefs(labelsStore);
    const headers = ref([
      { title: i18n.t("modals.applyGlobalMspPolicy.workspace"), key: "displayName" },
      { title: i18n.t("modals.applyGlobalMspPolicy.globalPolicies"), key: "globalPolicies" },
      { title: i18n.t("modals.applyGlobalMspPolicy.labels"), key: "labels" },
    ]);

    const showClearFilters = computed(() => {
      return !isEqual(localFilters.value, defaultApplyPolicyFilters);
    });

    const loadMoreWorkplaces = async () => {
      pagination.value = {
        page: pagination.value.page + 1,
        pageSize: 10,
      };
      await getItems();
    };

    const getItems = async () => {
      loading.value = true;
      try {
        const { data } = await globalPolicyStore.getWorkspacesToAssignPolicy({
          filters: localFilters.value,
          pagination: pagination.value,
          assignedMspPolicyId: props.config.item.id!,
        });
        workspaces.value = handleVirtualScrollData(workspaces.value, data.items, "workspaceId");
        total.value = data.total;
      } catch (e) {
        console.error(e);
      } finally {
        loading.value = false;
      }
    };

    const { onIntersect } = useInfiniteScroll(workspaces, { total }, loadMoreWorkplaces);

    const getGlobalPolicies = async (search: string = "") => {
      try {
        const { data } = await globalPolicyStore.searchGlobalPolicies(search);
        globalPolicies.value = data.items;
      } catch (e) {
        console.error(e);
      }
    };

    const updateFilters = (value: ApplyPolicyFilters, skipRequest = false) => {
      localFilters.value = value;

      if (skipRequest) return;

      // cancel pending call
      clearTimeout(workspaceTimer);
      // delay new call 500ms
      workspaceTimer = setTimeout(async () => {
        workspaces.value = [];
        pagination.value = {
          page: 0,
          pageSize: 10,
        };
        await getItems();
      }, 500);
    };

    const clearFilters = async () => {
      localFilters.value = cloneDeep(defaultApplyPolicyFilters);
      await getItems();
    };

    watch(
      () => localFilters.value,
      (newVal) => {
        updateFilters(newVal);
      },
      { deep: true }
    );

    watch(
      () => selectedItems.value,
      (newVal) => {
        if (newVal.length) {
          emit("update:localValue", {
            policyId: props.config.item.id!,
            workspaces: newVal,
          });
        }
      }
    );

    onMounted(async () => {
      await getItems();
      await labelsStore.getLabels(false);
      await getGlobalPolicies();
    });

    return {
      loading,
      workspaces,
      total,
      pagination,
      onIntersect,
      headers,
      selectedItems,
      labels,
      localFilters,
      globalPolicies,
      showClearFilters,
      clearFilters,
      iconsMap: {
        [WorkspaceType.REGULAR]: "icon-regular-plan-s",
        [WorkspaceType.CHANNEL]: "icon-parent-plan-s",
        [WorkspaceType.CHILD]: "icon-child-plan-s",
      },
    };
  },
};
</script>

<style lang="scss" scoped>
:deep(*) {
  .search-filter {
    .v-field__outline {
      display: none !important;
    }
  }

  .v-table__wrapper {
    height: 400px !important;
    border: 1px solid rgb(var(--v-theme-indigo-faint));
  }

  .v-table {
    box-shadow: unset !important;
  }
}

.table-row {
  height: 68px;
}

.v-row--selected {
  background-color: rgb(var(--v-theme-indigo-pale));
  td {
    border-bottom: 1px solid white !important;
  }
}

.filter-menu {
  width: 100px;
}

.labels-col {
  max-width: 300px;
  word-break: break-word;
  white-space: normal;
}

.policy-name-col {
  max-width: 150px;
  word-break: break-word;
  white-space: normal;
}
</style>
